import React, { useDeferredValue, useEffect, useState, type FC } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Grid from '@mui/material/Unstable_Grid2';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { RouterLink } from 'src/components/router-link';
import { Autocomplete, CircularProgress, MenuItem, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useDispatch, useSelector } from 'react-redux';
import {
  createPatientAction,
  editPatientAction,
  getAllPatientsListResetAction,
  getAllPhysicians,
  getAllTherapistAction,
  getPatientIdAction,
  getSinglePatient,
  resetCreatePatientAction,
  resetEditPatientAction,
} from 'src/redux/actions/patientAction';
import { useLocation, useNavigate } from 'react-router';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import { paths } from 'src/paths';
import {
  allowedCharactersRegexWithRegex,
  commonValidation,
  name,
} from 'src/pages/details-page/validations/customValidation';
import moment from 'moment';

interface CustomerEditFormProps {
  action: string;
  file: any;
}

interface CategoryOption {
  label: string;
  value: string;
}

const categoryOptions: CategoryOption[] = [
  {
    label: 'Male',
    value: 'MALE',
  },
  {
    label: 'Female',
    value: 'FEMALE',
  },
];

export const formatContactNumber = (value: string) => {
  if (value === null) {
    return {
      formattedValue: '',
      cleanedValue: '',
    };
  }

  const cleanedValue = value?.replace(/[^\d+]/g, '');

  const pattern = /^(\+\d{1,3})?(\d{3})?(\d{3})(\d{4})$/;

  const formattedValue = cleanedValue?.replace(pattern, (match, p1, p2, p3, p4) => {
    if (p2 && p3) {
      if (p1) {
        return `${p1} (${p2})-${p3}-${p4}`;
      } else {
        return `(${p2})-${p3}-${p4}`;
      }
    } else {
      return cleanedValue;
    }
  });

  return {
    formattedValue,
    cleanedValue,
  };
};

function getCurrentDateUTCWithoutTime(date: any) {
  // Get the date components
  const utcDateFormate = moment(date).format('YYYY-MM-DD');
  // Construct the UTC date string with time portion set to 00:00:00.000Z
  const utcDateString = `${utcDateFormate}T00:00:00.000Z`;

  return utcDateString;
}

const convertToUTC = (selectedDate: any) => {
  const selectedDateObj = new Date(selectedDate);
  const selectedYear = selectedDateObj.getFullYear();
  const selectedMonth = selectedDateObj.getMonth();
  const selectedDay = selectedDateObj.getDate();

  const systemDateObj = new Date();
  const systemHours = systemDateObj.getHours();
  const systemMinutes = systemDateObj.getMinutes();
  const systemSeconds = systemDateObj.getSeconds();

  // Create combined Date object
  const combinedDate = new Date(
    selectedYear,
    selectedMonth,
    selectedDay,
    systemHours,
    systemMinutes,
    systemSeconds
  );

  return combinedDate?.toISOString();
};

export const CustomerEditForm: FC<CustomerEditFormProps> = (props) => {
  const { action, file } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const patientId = state?.patientId;
  const [search, setSearch] = useState('');
  const [timer, setTimer] = useState<any>(undefined);

  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const createPatientData = useSelector((state: any) => state?.createPatientReducer);
  const editPatientData = useSelector((state: any) => state?.editPatientReducer);
  const allTherapistDisciplineResponse = useSelector(
    (state: any) => state?.getAllTherapistDisciplineReducer
  );
  const physicianData = useSelector(
    (state: any) => state?.getEnterprisePhysicianReducer?.enterprisePhysician?.content
  );
  const [pageNo, setPageNo] = useState(0);

  const handleSearchInputChange = (e: any) => {
    if (timer) {
      clearTimeout(timer);
    }
    setPageNo(0);
    const timeout = setTimeout(() => {
      setSearch(e.target.value);
    }, 1000);

    setTimer(timeout);
  };

  const [physicianList, setPhysicianList] = useState<any>([]);

  const physicianResponse = useSelector((state: any) => state?.getEnterprisePhysicianReducer);

  useEffect(() => {
    dispatch(getAllTherapistAction());
  }, []);

  useEffect(() => {
    if (patientData) setFieldValue(new Date(patientData?.dateOfBirth));
  }, [patientData]);
  const [fieldValue, setFieldValue] = useState<Date | null>();
  const [patientError, setPatientError] = useState('');
  const { patients, patientDetails } = paths;
  const facilityId = localStorage.getItem('facilityId');

  useEffect(() => {
    if (!Array.isArray(physicianData)) return;
    if (pageNo === 0 && physicianData) setPhysicianList(physicianData);
    else setPhysicianList((prev: any) => [...prev, ...physicianData]);
  }, [physicianData]);

  // console.log(physicianData)

  const loaderMorePhysician = () => {
    if (!physicianResponse?.enterprisePhysician?.last && search === '') {
      setPageNo((prev) => prev + 1);
    }
  };

  useEffect(() => {
    dispatch(
      getAllPhysicians({
        facilityId: facilityId,
        pageSize: 30,
        page: pageNo,
        search: search,
      })
    );
  }, [search, pageNo]);
  const saveToLocalStorage = (key: any, value: any) => {
    localStorage.setItem(key, JSON.stringify(value));
  };

  const getFromLocalStorage = (key: any) => {
    const storedValue = localStorage.getItem(key);

    if (storedValue !== undefined && storedValue !== null) {
      try {
        return JSON.parse(storedValue);
      } catch (error) {
        console.error(`Error parsing JSON for key '${key}':`, error);
        return null;
      }
    }
    return null;
  };
  useEffect(() => {
    if (patientData) {
      saveToLocalStorage('gender', patientData?.gender);
      saveToLocalStorage('primaryPhysician', patientData?.physicianEntitySet[0]);
    }
  }, [patientData]);

  const removeDashes = (ssn: any) => {
    return ssn ? ssn.replace(/-/g, '') : '';
  };

  const initiaGenderValue = getFromLocalStorage('gender') || '';
  const initialPrimaryPhysicianValue = getFromLocalStorage('primaryPhysician') || '';
  const dobWithoutTimeZone = patientData?.dateOfBirth?.split('T')?.at(0);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: action === 'Create' ? '' : patientData?.firstName?.trim(),
      lastName: action === 'Edit' ? patientData?.lastName?.trim() : '',
      dateOfBirth:
        action === 'Edit'
          ? patientData?.dateOfBirth
            ? new Date(
                new Date(dobWithoutTimeZone).valueOf() +
                  new Date(dobWithoutTimeZone).getTimezoneOffset() * 60 * 1000
              )
            : ''
          : null,
      gender: action === 'Edit' ? initiaGenderValue : '',
      primaryPhysician:
        action === 'Edit'
          ? patientData?.physicianEntitySet?.length > 0
            ? patientData?.physicianEntitySet[0]?.uuid
            : ''
          : '',
      otTherapist: action === 'Edit' ? patientData?.otTherapist : '',
      ptTherapist: action === 'Edit' ? patientData?.ptTherapist : '',
      slpTherapist: action === 'Edit' ? patientData?.slpTherapist : '',
      medicalRecordNumber: action === 'Edit' ? patientData?.medicalRecordNumber?.trim() : '',
      bedAssignment: action === 'Edit' ? patientData?.bedAssignment?.trim() : '',
      socialSecurityNumber:
        action === 'Edit' ? removeDashes(patientData?.socialSecurityNumber?.trim()) : '',
      roomNumber: action === 'Edit' ? removeDashes(patientData?.roomNumber?.trim()) : '',
      floorNumber: action === 'Edit' ? removeDashes(patientData?.floorNumber?.trim()) : '',
      patientEmail: action === 'Create' ? '' : patientData?.patientEmail?.trim(),
      patientPhoneNo:
        action === 'Create'
          ? ''
          : formatContactNumber(patientData?.patientPhoneNo?.trim()).formattedValue,
      ptSlpCharges:
        action === 'Create'
          ? ''
          : patientData?.ptSlpCharges === 0 || patientData?.ptSlpCharges
          ? patientData?.ptSlpCharges
          : 0,
      otCharges:
        action === 'Create'
          ? ''
          : patientData?.otCharges === 0 || patientData?.otCharges
          ? patientData?.otCharges
          : 0,
    },
    validationSchema: Yup.object({
      firstName: commonValidation.customValidation(
        'First Name',
        true,
        true,
        false,
        allowedCharactersRegexWithRegex,
        2,
        64
      ),
      lastName: commonValidation.customValidation(
        'Last Name',
        true,
        true,
        false,
        allowedCharactersRegexWithRegex,
        2,
        64
      ),
      dateOfBirth: Yup.date().required('Please select the Date Of Birth'),
      gender: Yup.string().required('Please select the Gender'),
      primaryPhysician: Yup.string().max(255).required('Please select the Primary Physician'),
      otTherapist: Yup.string().nullable(),
      ptTherapist: Yup.string().nullable(),
      slpTherapist: Yup.string().nullable(),
      medicalRecordNumber: Yup.string()
        .required('Please enter a valid Medical Record Number')
        .matches(/^[a-zA-Z0-9]+$/, 'Medical Record Number must contain only alphanumeric'),

      bedAssignment: Yup.string().max(255),
      socialSecurityNumber: Yup.string()
        .matches(/^[0-9]+$/, 'Social Security Number must contain only numeric digits')
        .min(9, 'Social Security Number must be equal to 9')
        .max(9, 'Social Security Number must be at most 9 characters'),

      roomNumber: Yup.string().matches(/^[0-9]+$/, 'Room Number must contain only numeric digits'),
      floorNumber: Yup.string().matches(
        /^[0-9]+$/,
        'Floor Number must contain only numeric digits'
      ),
      patientEmail: Yup.string().email('Must be a valid email').max(255),
      patientPhoneNo: Yup.string().matches(/^(\+\d{1,3})?\s?\(?\d{3}\)?-?\s?\d{3}-?\d{4}$/, {
        excludeEmptyString: true,
        message: 'Please Enter a valid phone Number',
      }),
      otCharges: Yup.string().required('Please enter a valid OT charges'),
      ptSlpCharges: Yup.string().required('Please enter a valid PT+SLP charges'),
    }),
    onSubmit: async (values, helpers): Promise<void> => {
      const primaryPhysician = physicianData?.filter(
        (physician: any) => values?.primaryPhysician === physician?.uuid
      );
      const updatedData = {
        firstName: values.firstName?.trim(),
        lastName: values.lastName?.trim(),
        dateOfBirth: getCurrentDateUTCWithoutTime(values.dateOfBirth || new Date()),
        primaryPhysician: primaryPhysician?.length > 0 ? primaryPhysician[0]?.physicianName : '',
        profileImage: file,
        gender: values.gender,
        physicianIds: [values.primaryPhysician],
        otCharges: values?.otCharges,
        ptSlpCharges: values?.ptSlpCharges,
        medicalRecordNumber: values.medicalRecordNumber,
        bedAssignment: values.bedAssignment,
        socialSecurityNumber: values.socialSecurityNumber ? values.socialSecurityNumber : null,
        floorNumber: values.floorNumber ? values.floorNumber : null,
        roomNumber: values.roomNumber ? values.roomNumber : null,
        patientEmail: values?.patientEmail,
        patientPhoneNo: formatContactNumber(values?.patientPhoneNo).cleanedValue,
        otTherapist: values.otTherapist,
        ptTherapist: values.ptTherapist,
        slpTherapist: values.slpTherapist,
      };
      if (action === 'Create') dispatch(createPatientAction(updatedData));
      else
        dispatch(
          editPatientAction({
            ...updatedData,
            uuid: patientId,
          })
        );
    },
  });

  useEffect(() => {
    if (action === 'Edit')
      dispatch(
        getSinglePatient({
          patientId: patientId,
        })
      );

    dispatch(resetEditPatientAction());
  }, []);

  useEffect(() => {
    if (!createPatientData.isPatientCreated && createPatientData.response === null) return;
    if (!createPatientData.isPatientCreated && createPatientData.response !== null) {
      setPatientError(createPatientData?.response?.message);
      setTimeout(() => {
        setPatientError('');
        dispatch(resetCreatePatientAction());
      }, 8000);
      return;
    }
    if (createPatientData?.isPatientCreated) setPatientError('');
    setTimeout(() => {
      dispatch(resetCreatePatientAction());

      if (createPatientData?.response?.data) {
        dispatch(getAllPatientsListResetAction());
        dispatch(getPatientIdAction(createPatientData?.response?.data));
        navigate(`${patients}/${patientDetails}`);
        return;
      }
      dispatch(resetCreatePatientAction());
      navigate(`${patients}`);
    }, 3000);
    return;
  }, [createPatientData]);

  useEffect(() => {
    if (!editPatientData.isPatientEdited && editPatientData.response === null) return;
    if (!editPatientData.isPatientEdited && editPatientData.response !== null) {
      setPatientError(editPatientData?.response?.message);
      setTimeout(() => {
        setPatientError('');
        dispatch(resetCreatePatientAction());
      }, 8000);
      return;
    }
    if (editPatientData?.isPatientEdited) setPatientError('');
    setTimeout(() => {
      dispatch(getAllPatientsListResetAction());
      dispatch(resetEditPatientAction());
      navigate(`${patients}/${patientDetails}`);
    }, 3000);
    return;
  }, [editPatientData]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <Card>
        <CardHeader title={action === 'Edit' ? 'Edit Basic Details' : 'Add Basic Details'} />
        <CardContent sx={{ py: 0 }}>
          <Grid
            container
            spacing={3}
            padding={'20px 0px'}
          >
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={!!(formik.touched.firstName && formik.errors.firstName)}
                fullWidth
                helperText={formik.touched.firstName && (formik.errors.firstName as any)}
                label="First Name"
                name="firstName"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.firstName}
                InputLabelProps={{
                  shrink:
                    formik.values.firstName !== '' && !formik.touched.firstName ? true : undefined,
                }}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={!!(formik.touched.lastName && formik.errors.lastName)}
                fullWidth
                helperText={formik.touched.lastName && (formik.errors.lastName as any)}
                label="Last Name"
                name="lastName"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.lastName}
                InputLabelProps={{
                  shrink:
                    formik.values.lastName !== '' && !formik.touched.lastName ? true : undefined,
                }}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={formik.touched.gender && Boolean(formik.errors.gender)}
                fullWidth
                helperText={
                  formik.touched.gender &&
                  formik.errors.gender &&
                  typeof formik.errors.gender === 'string' &&
                  formik.errors.gender
                }
                label="Gender"
                name="gender"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.gender}
                select
              >
                {categoryOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <DatePicker
                onChange={(newDate) => {
                  formik.setFieldValue('dateOfBirth', newDate);
                }}
                label="Date Of Birth"
                value={formik.values.dateOfBirth}
                format="MM dd, yyyy"
                disableFuture
                sx={{
                  width: '100%',
                  border:
                    formik.touched.dateOfBirth && formik.errors.dateOfBirth ? '2px solid red' : '',
                  borderRadius:
                    formik.touched.dateOfBirth && formik.errors.dateOfBirth ? '10px' : '',
                }}
              />
              {formik.touched.dateOfBirth && formik.errors.dateOfBirth && (
                <Typography
                  padding={'0px 16px'}
                  color={'red'}
                  variant="caption"
                >
                  {formik.errors.dateOfBirth}
                </Typography>
              )}
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={!!(formik.touched.patientPhoneNo && formik.errors.patientPhoneNo)}
                fullWidth
                helperText={formik.touched.patientPhoneNo && (formik.errors.patientPhoneNo as any)}
                label="Phone Number"
                name="patientPhoneNo"
                onBlur={formik.handleBlur}
                onChange={(e) =>
                  formik.setFieldValue(
                    'patientPhoneNo',
                    formatContactNumber(e.target.value).formattedValue
                  )
                }
                value={formik.values.patientPhoneNo}
                InputLabelProps={{
                  shrink:
                    formik.values.patientPhoneNo !== '' && !formik.touched.patientPhoneNo
                      ? true
                      : undefined,
                }}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={!!(formik.touched.patientEmail && formik.errors.patientEmail)}
                fullWidth
                helperText={formik.touched.patientEmail && (formik.errors.patientEmail as any)}
                label="Email"
                name="patientEmail"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.patientEmail}
                InputLabelProps={{
                  shrink:
                    formik.values.patientEmail !== '' && !formik.touched.patientEmail
                      ? true
                      : undefined,
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
        <CardHeader title={action === 'Edit' ? 'Edit Therapy Details' : 'Add Therapy Details'} />
        <CardContent sx={{ py: 0 }}>
          <Grid
            container
            spacing={3}
            padding={'20px 0px'}
          >
            <Grid
              xs={12}
              md={6}
            >
              <Autocomplete
                disablePortal
                onBlur={formik.handleBlur}
                getOptionLabel={(option: any) => option?.physicianName || ''}
                onChange={(event, newValue: any) => {
                  formik.setFieldValue('primaryPhysician', newValue?.uuid || '');
                }}
                filterOptions={(x) => x}
                // clearOnBlur={false}
                disableCloseOnSelect
                onClose={(event) => setSearch('')}
                isOptionEqualToValue={(option: any, value: any) => {
                  return option?.uuid === value?.uuid;
                }}
                loading={physicianResponse?.loading}
                value={
                  physicianList?.find(
                    (physician: any) => physician.uuid === formik.values.primaryPhysician
                  ) || null
                }
                renderOption={(props, option) => {
                  return (
                    <MenuItem
                      key={option?.uuid}
                      value={option?.uuid}
                      {...props}
                    >
                      {option?.physicianName}
                    </MenuItem>
                  );
                }}
                ListboxProps={{
                  style: { maxHeight: '300px', overflow: 'auto' },
                  role: 'closeMenu',
                  onScroll: (event) => {
                    let scrollThreshold = 1;
                    if (
                      Math.abs(
                        (event.target as any).scrollHeight -
                          (event.target as any).scrollTop -
                          (event.target as any).clientHeight
                      ) <= scrollThreshold
                    ) {
                      loaderMorePhysician();
                    }
                  },
                }}
                options={physicianList || []}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {physicianResponse?.loading ? (
                            <CircularProgress
                              color="inherit"
                              size={20}
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    error={!!(formik.touched.primaryPhysician && formik.errors.primaryPhysician)}
                    helperText={
                      formik.touched.primaryPhysician && (formik.errors.primaryPhysician as any)
                    }
                    onChange={handleSearchInputChange}
                    label="Primary Physician"
                  />
                )}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <Autocomplete
                disablePortal
                onBlur={formik.handleBlur}
                getOptionLabel={(option: any) => option?.name || ''}
                onChange={(event, newValue: any) => {
                  formik.setFieldValue('otTherapist', newValue?.uuid);
                }}
                isOptionEqualToValue={(option: any, value: any) => {
                  return option?.uuid === value?.uuid;
                }}
                loading={allTherapistDisciplineResponse?.loading}
                value={
                  allTherapistDisciplineResponse?.otResponse?.find(
                    (therapist: any) => therapist.uuid === formik.values.otTherapist
                  ) || null
                }
                renderOption={(props, option) => {
                  return (
                    <MenuItem
                      key={option?.uuid}
                      value={option?.uuid}
                      {...props}
                    >
                      {option?.name}
                    </MenuItem>
                  );
                }}
                options={allTherapistDisciplineResponse?.otResponse || []}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {allTherapistDisciplineResponse?.loading ? (
                            <CircularProgress
                              color="inherit"
                              size={20}
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    label="Treating Therapist-OT"
                  />
                )}
              />
            </Grid>

            <Grid
              xs={12}
              md={6}
            >
              <Autocomplete
                disablePortal
                onBlur={formik.handleBlur}
                getOptionLabel={(option: any) => option?.name || ''}
                onChange={(event, newValue: any) => {
                  formik.setFieldValue('ptTherapist', newValue?.uuid);
                }}
                isOptionEqualToValue={(option: any, value: any) => {
                  return option?.uuid === value?.uuid;
                }}
                loading={allTherapistDisciplineResponse?.loading}
                value={
                  allTherapistDisciplineResponse?.ptResponse?.find(
                    (therapist: any) => therapist.uuid === formik.values.ptTherapist
                  ) || null
                }
                renderOption={(props, option) => {
                  return (
                    <MenuItem
                      key={option?.uuid}
                      value={option?.uuid}
                      {...props}
                    >
                      {option?.name}
                    </MenuItem>
                  );
                }}
                options={allTherapistDisciplineResponse?.ptResponse || []}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {allTherapistDisciplineResponse?.loading ? (
                            <CircularProgress
                              color="inherit"
                              size={20}
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    label="Treating Therapist-PT"
                  />
                )}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <Autocomplete
                disablePortal
                onBlur={formik.handleBlur}
                getOptionLabel={(option: any) => option?.name || ''}
                onChange={(event, newValue: any) => {
                  formik.setFieldValue('slpTherapist', newValue?.uuid);
                }}
                isOptionEqualToValue={(option: any, value: any) => {
                  return option?.uuid === value?.uuid;
                }}
                loading={allTherapistDisciplineResponse?.loading}
                value={
                  allTherapistDisciplineResponse?.slpResponse?.find(
                    (therapist: any) => therapist.uuid === formik.values.slpTherapist
                  ) || null
                }
                renderOption={(props, option) => {
                  return (
                    <MenuItem
                      key={option?.uuid}
                      value={option?.uuid}
                      {...props}
                    >
                      {option?.name}
                    </MenuItem>
                  );
                }}
                options={allTherapistDisciplineResponse?.slpResponse || []}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {allTherapistDisciplineResponse?.loading ? (
                            <CircularProgress
                              color="inherit"
                              size={20}
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    label="Treating Therapist-SLP"
                  />
                )}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={!!(formik.touched.bedAssignment && formik.errors.bedAssignment)}
                fullWidth
                helperText={formik.touched.bedAssignment && (formik.errors.bedAssignment as any)}
                label="Bed Assignment"
                name="bedAssignment"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.bedAssignment}
                InputLabelProps={{
                  shrink:
                    formik.values.bedAssignment !== '' && !formik.touched.bedAssignment
                      ? true
                      : undefined,
                }}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={!!(formik.touched.medicalRecordNumber && formik.errors.medicalRecordNumber)}
                fullWidth
                helperText={
                  formik.touched.medicalRecordNumber && (formik.errors.medicalRecordNumber as any)
                }
                label="Medical Record Number"
                name="medicalRecordNumber"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.medicalRecordNumber}
                InputLabelProps={{
                  shrink:
                    formik.values.medicalRecordNumber !== '' && !formik.touched.medicalRecordNumber
                      ? true
                      : undefined,
                }}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={
                  !!(formik.touched.socialSecurityNumber && formik.errors.socialSecurityNumber)
                }
                fullWidth
                helperText={
                  formik.touched.socialSecurityNumber && (formik.errors.socialSecurityNumber as any)
                }
                label="Social Security Number"
                name="socialSecurityNumber"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.socialSecurityNumber}
                InputLabelProps={{
                  shrink:
                    formik.values.socialSecurityNumber !== '' &&
                    !formik.touched.socialSecurityNumber
                      ? true
                      : undefined,
                }}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={!!(formik.touched.floorNumber && formik.errors.floorNumber)}
                fullWidth
                helperText={formik.touched.floorNumber && (formik.errors.floorNumber as any)}
                label="Floor Number"
                name="floorNumber"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.floorNumber}
                InputLabelProps={{
                  shrink:
                    formik.values.floorNumber !== '' && !formik.touched.floorNumber
                      ? true
                      : undefined,
                }}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                error={!!(formik.touched.roomNumber && formik.errors.roomNumber)}
                fullWidth
                helperText={formik.touched.roomNumber && (formik.errors.roomNumber as any)}
                label="Room Security Number"
                name="roomNumber"
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
                value={formik.values.roomNumber}
                InputLabelProps={{
                  shrink:
                    formik.values.roomNumber !== '' && !formik.touched.roomNumber
                      ? true
                      : undefined,
                }}
              />
            </Grid>
          </Grid>
        </CardContent>

        <CardHeader title="Medicare Part B Charges To Date" />
        <CardContent sx={{ py: 0 }}>
          <Grid
            container
            spacing={3}
            padding={'20px 0px'}
          >
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                label="PT+SLP ($)"
                fullWidth
                error={!!(formik.touched.ptSlpCharges && formik.errors.ptSlpCharges)}
                helperText={formik.touched.ptSlpCharges && (formik.errors.ptSlpCharges as any)}
                name="ptSlpCharges"
                onBlur={formik.handleBlur}
                onChange={(event) => {
                  if (/^[0-9]*$/.test(event.target.value)) formik.handleChange(event);
                }}
                value={formik.values.ptSlpCharges}
                InputLabelProps={{
                  shrink:
                    formik.values.ptSlpCharges !== '' && !formik.touched.ptSlpCharges
                      ? true
                      : undefined,
                }}
              />
            </Grid>
            <Grid
              xs={12}
              md={6}
            >
              <TextField
                label="OT ($)"
                fullWidth
                error={!!(formik.touched.otCharges && formik.errors.otCharges)}
                helperText={formik.touched.otCharges && (formik.errors.otCharges as any)}
                name="otCharges"
                onBlur={formik.handleBlur}
                onChange={(event) => {
                  if (/^[0-9]*$/.test(event.target.value)) formik.handleChange(event);
                }}
                value={formik.values.otCharges}
                InputLabelProps={{
                  shrink:
                    formik.values.otCharges !== '' && !formik.touched.otCharges ? true : undefined,
                }}
              />
            </Grid>
          </Grid>
        </CardContent>
        {(!createPatientData?.isPatientCreated || !editPatientData?.isPatientEdited) && (
          <Typography
            padding={'0px 20px'}
            variant="h6"
            color="red"
          >
            {patientError}
          </Typography>
        )}
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          flexWrap="wrap"
          justifyContent={'flex-end'}
          spacing={3}
          sx={{ p: 3 }}
        >
          <Button
            color="inherit"
            component={RouterLink}
            disabled={formik.isSubmitting}
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button
            disabled={formik.isSubmitting || createPatientData?.loading || editPatientData?.loading}
            type="submit"
            variant="contained"
          >
            Save
          </Button>
        </Stack>
      </Card>
      {(createPatientData?.isPatientCreated || editPatientData.isPatientEdited) && (
        <SimpleSnackbar
          message={
            createPatientData?.isPatientCreated
              ? 'New Patient is added successfully'
              : 'Patient details are updated successfully!'
          }
        />
      )}
    </form>
  );
};
